import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

/**
 * TooltipProvider wraps the application to provide tooltip context.
 * Should be placed at a high level in the component tree.
 */
const TooltipProvider = TooltipPrimitive.Provider

/**
 * Tooltip root component that controls the tooltip state.
 * Use with TooltipTrigger and TooltipContent.
 */
const Tooltip = TooltipPrimitive.Root

/**
 * TooltipTrigger component that triggers the tooltip on hover or focus.
 * Wraps the element that should show the tooltip.
 */
const TooltipTrigger = TooltipPrimitive.Trigger

/**
 * TooltipContent component displays the tooltip content.
 * Built on Radix UI Tooltip primitive with customizable styling.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>} props - Tooltip content props
 * @param {number} [props.sideOffset=4] - Distance from the trigger element
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Tooltip content element
 * 
 * @example
 * ```tsx
 * <TooltipProvider>
 *   <Tooltip>
 *     <TooltipTrigger>Hover me</TooltipTrigger>
 *     <TooltipContent>
 *       <p>Tooltip text</p>
 *     </TooltipContent>
 *   </Tooltip>
 * </TooltipProvider>
 * ```
 */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
